import { createEffect, createSignal, type Accessor } from 'solid-js'
import { nextUtcMidnightMs } from '~/lib/countdown'
import { logNonFatal } from '~/lib/diagnostics'
import { errorDetail, type ShopBootstrap } from '~/services/bootstrap'
import {
  loadReplacementDailyPicksGrid,
  type DailyPicksGrid,
} from './daily-picks-grid'

// The daily-pick rollover (ADR 0028, GLOSSARY.md "Daily-pick rollover"): when
// the Shop server clock crosses UTC midnight mid-session, refetch the
// daily-pick collection — only it — hydrate a replacement grid off-screen,
// and swap it in as one signal write once every slot settles, leaving
// foreground dialogs, in-flight purchases, balances, ownership, and the hero
// row untouched. One attempt per midnight: success re-arms to the next
// midnight; failure fails closed into the general error dialog the caller renders
// (dismissal closes Shop), never retrying.
export function createDailyPickRollover(options: {
  bootstrap: Accessor<ShopBootstrap | undefined>
  onSwap: (grid: DailyPicksGrid) => void
  serverNow: Accessor<number>
}): { error: Accessor<string | undefined> } {
  const [error, setError] = createSignal<string>()
  let targetMs: number | undefined
  let armed = true

  const attempt = async (data: ShopBootstrap) => {
    try {
      options.onSwap(await loadReplacementDailyPicksGrid(data))
      targetMs = nextUtcMidnightMs(options.serverNow())
      armed = true
    } catch (cause) {
      // The attempt boundary is the one labeling point: every failure —
      // either fetch leg or anything after — reaches the general error dialog under the
      // rollover-identifying code, bootstrap-style `label: detail`.
      const code = `daily_pick_rollover: ${errorDetail(cause)}`
      logNonFatal('shop_daily_pick_rollover_failed', { detail: code })
      setError(code)
    }
  }

  createEffect(() => {
    const now = options.serverNow()
    const data = options.bootstrap()
    if (!data) return
    // Arm when bootstrap lands: a midnight already behind the session's
    // first assortment needs no rollover.
    targetMs ??= nextUtcMidnightMs(now)
    if (!armed || now < targetMs) return
    armed = false
    void attempt(data)
  })

  return { error }
}
